import type { AgentTurnRequest } from './types';

const POLL_INTERVAL_MS = 1000;
const SLOW_POLL_INTERVAL_MS = 5000;
const SLOW_POLL_AFTER_MS = 180_000;

const wait = (milliseconds: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const complete = () => {
            signal?.removeEventListener('abort', abort);
            resolve();
        };
        const timer = setTimeout(complete, milliseconds);
        const abort = () => {
            clearTimeout(timer);
            signal?.removeEventListener('abort', abort);
            reject(new DOMException('The operation was aborted.', 'AbortError'));
        };
        if (signal?.aborted) abort();
        else signal?.addEventListener('abort', abort, { once: true });
    });

/** Elapsed time does not settle a server request. Keep monitoring until its state is terminal or the caller leaves. */
export const pollAgentRequest = async (
    readRequest: () => Promise<AgentTurnRequest>,
    signal?: AbortSignal,
    onProgress?: (request: AgentTurnRequest) => void
) => {
    const startedAt = Date.now();
    while (true) {
        if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        const request = await readRequest();
        if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
        onProgress?.(request);
        if (request.state === 'completed' || request.state === 'failed' || request.state === 'cancelled') return request;
        const interval = !request.cancellationRequested && Date.now() - startedAt >= SLOW_POLL_AFTER_MS ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
        await wait(interval, signal);
    }
};
